/**
 * Emergency contacts (piece 34, *The employee file*; decision 3184; T2072):
 * one or two people an employee names to call, kept by the employee on My
 * Info and drawn in the Employee page's Emergency section. 🔒 HR and the
 * employee read them there; the management chain reads them only through a
 * reason form, whose reason is kept and whose read raises a To-Do on HR.
 */

/** An employee names one or two. */
export const EMERGENCY_CONTACT_LIMIT = 2;

/** The employee's own write from My Info, relative to the API root. */
export const MY_EMERGENCY_CONTACTS_PATH = '/hr/my-info/emergency-contacts';

/** A manager's reasoned read of one job's contacts, relative to the API root. */
export function emergencyContactsReadPath(employmentId: string): string {
  return `/hr/employees/${encodeURIComponent(employmentId)}/emergency-contacts/read`;
}

/** 🔒 The To-Do each chain read raises on every HR holder, closed by a tick (piece 9's kinds). */
export const EMERGENCY_CONTACT_READ_KIND = 'EmergencyContactRead';
/** The record the To-Do names: the employment whose contacts were read. */
export const EMERGENCY_CONTACT_READ_SOURCE = 'EmploymentRecord';

/** 🔒 The refusal of a read to anybody outside the chain, in a sentence. */
export const EMERGENCY_READ_REFUSAL = 'Emergency contacts are read by HR, the employee and the managers above them, and this identity is none of those.';

/** One contact as the employee writes it; the stored phone is E.164. */
export interface EmergencyContactDraft {
  name: string;
  relationship: string;
  phoneNumber: string;
}

/**
 * A contact the employee's form held and the save drops, with the version the form read it at (T2284). 🔨 A removal
 * names the version an edit does, so a stale form cannot delete a contact changed somewhere else.
 */
export interface EmergencyContactRemoval {
  rank: number;
  version: number;
}

/** One contact as it is read, with its rank and the version an edit names. */
export interface EmergencyContactRow extends EmergencyContactDraft {
  id: string;
  rank: number;
  version: number;
}

/** What the Emergency section carries beside its rows. */
export interface EmergencySection {
  /** 🔒 The chain receives no rows and reads them through the reason form. */
  behindReason: boolean;
  /** The employee on My Info keeps them, and their contacts arrive to edit. */
  contacts: EmergencyContactRow[] | null;
}

/** The answer to a reasoned read and to the employee's write. */
export interface EmergencyContactsAnswer {
  contacts: EmergencyContactRow[];
}

/** The longest name or relationship kept. */
export const EMERGENCY_TEXT_MAXIMUM = 120;

/** The contacts' problems, each one sentence; empty when the set saves. An empty set clears them. */
export function emergencyContactProblems(contacts: readonly EmergencyContactDraft[]): string[] {
  const problems: string[] = [];
  if (contacts.length > EMERGENCY_CONTACT_LIMIT) problems.push('An employee names one or two emergency contacts.');
  contacts.forEach((contact, index) => {
    const which = index === 0 ? 'The first contact' : 'The second contact';
    if (!contact.name.trim()) problems.push(`${which} needs a name.`);
    if (!contact.relationship.trim()) problems.push(`${which} needs a relationship.`);
    if (!contact.phoneNumber.trim()) problems.push(`${which} needs a phone number.`);
    if (contact.name.length > EMERGENCY_TEXT_MAXIMUM || contact.relationship.length > EMERGENCY_TEXT_MAXIMUM) problems.push(`${which} is at most ${EMERGENCY_TEXT_MAXIMUM} characters a field.`);
  });
  return problems;
}

/** The longest reason kept. */
export const EMERGENCY_REASON_MAXIMUM = 500;

/** 🔒 A manager's reason is a sentence of at least three words, so "x" is not a reason on the record. */
export function emergencyReasonProblem(reason: string): string | null {
  const words = reason.trim().split(/\s+/).filter(Boolean);
  if (words.length < 3) return 'The reason is a sentence of at least three words.';
  if (reason.length > EMERGENCY_REASON_MAXIMUM) return `The reason is at most ${EMERGENCY_REASON_MAXIMUM} characters.`;
  return null;
}
